import WelcomeNavbar from "../../components/WelcomeNavbar/index.jsx";
import { Button } from "@heroui/react";
import useStableViewportHeight from "../../hooks/useStableViewportHeight.js";
import { ChevronDown, Lock, ChevronRight, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Feature1, Feature2, Feature3, Feature4, Feature5 } from "./subcomponents/features.jsx";
import { FooterLogo } from "./subcomponents/demos.jsx";
import { LandingPageModal } from "../../components/LandingPagePopup/index.jsx";
import { motion, useScroll, useTransform } from "framer-motion";

import "./styles.css";


const getClass = (vh, bigger, smaller) => {
    return vh && vh <= 700 ? smaller : vh && vh <= 1000 ? bigger : ''
}

const HeroText = ({ vh }) => {
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2,
            },
        },
    };

    const item = {
        hidden: { opacity: 0, y: 6 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3, ease: "easeOut" },
        },
    };

    return (
        <>
            <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className={`md:hidden absolute left-0 flex flex-col gap-4 pl-5`}
                style={{
                    top: `${vh < 900 ? vh / 8 : vh / 7}px`
                }}
            >
                <motion.div variants={item} className="rounded-full bg-[#9FC183] w-6 h-6" />

                <div className="text-white flex flex-col gap-1 text-[16px] tracking-wide leading-[1.6]">
                    <motion.p variants={item}>英語が話せないのは、</motion.p>
                    <motion.p variants={item}>才能の問題じゃない。</motion.p>
                    <motion.p variants={item}>理解しようとした先に、</motion.p>
                    <motion.p variants={item}>問いが生まれる。</motion.p>
                    <motion.p variants={item}>その問いの深さが、</motion.p>
                    <motion.p variants={item}>そのまま言葉の深さになる。</motion.p>
                </div>

                <motion.p variants={item} className="mt-4 text-white text-[16px] tracking-wide">
                    アオメと共に。
                </motion.p>
            </motion.div>

            <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className={`hidden md:block absolute top-1/3 lg:top-1/4 left-0 flex flex-col gap-4 ml-[10vw] lg:ml-[15vw]`}
            >
                <motion.div variants={item} className="rounded-full bg-[#9FC183] w-6 h-6 " />

                <div className="mt-2 text-white flex flex-col gap-2 text-[20px] lg:text-[24px] [@media(max-height:600px)]:text-[16px] tracking-wide leading-[1.6]">
                    <motion.p variants={item}>英語が話せないのは、才能の問題じゃない。</motion.p>
                    <motion.p variants={item}>理解しようとした先に、問いが生まれる。</motion.p>
                    <motion.p variants={item}>その問いの深さが、そのまま言葉の深さになる。</motion.p>
                </div>

                <motion.p variants={item} className="mt-4 text-white text-[20px] lg:text-[24px] tracking-wide">
                    アオメと共に。
                </motion.p>
            </motion.div>

        </>
    )
}
const Cta = ({ vh, navigate }) => {
    // container now takes a base delay and applies it before children start staggering
    const container = (baseDelay = 0) => ({
        hidden: {},
        show: {
            transition: {
                delayChildren: baseDelay,
                staggerChildren: 0, // keep 0 if you don't want extra stagger between line/text/button
            },
        },
    });

    const line = (baseDelay = 0) => ({
        hidden: { scaleY: 0 },
        show: {
            scaleY: 1,
            transition: { duration: 0.5, ease: "easeOut", delay: baseDelay },
        },
    });

    const slideLeft = (baseDelay = 0) => ({
        hidden: { opacity: 0, x: 10 },
        show: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeOut", delay: baseDelay + 0.5 },
        },
    });

    const slideRight = (baseDelay = 0) => ({
        hidden: { opacity: 0, x: -10 },
        show: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeOut", delay: baseDelay + 0.5 },
        },
    });

    const mobileDelay = 1;
    const desktopDelay = 0.5;

    return (
        <>
            {/* Mobile */}
            <motion.div
                variants={container(mobileDelay)}
                initial="hidden"
                animate="show"
                className={`md:hidden absolute ${getClass(vh, "top-[580px]", "top-[510px]")} right-0 flex items-center justify-center w-full gap-3`}
            >
                <motion.div variants={slideLeft(mobileDelay)} className="text-base-text text-sm">
                    <p>14日間、共に。</p>
                    <p>カード登録不要。</p>
                </motion.div>

                <motion.div
                    variants={line(mobileDelay)}
                    style={{ transformOrigin: "top" }}
                    className="h-[50px] w-[1px] bg-[#9FC183]"
                />

                <motion.div variants={slideRight(mobileDelay)}>
                    <Button 
                        className="bg-[#27485C] text-[#F2EACE] font-light tracking-wider" 
                        // onPress={() => navigate("/signup")}
                    >
                        aomeを体験
                    </Button>
                </motion.div>
            </motion.div>

            {/* Desktop */}
            <motion.div
                variants={container(desktopDelay)}
                initial="hidden"
                animate="show"
                className={`hidden md:flex absolute top-[60svh] lg:top-[60svh] right-0 items-center justify-center w-full gap-3 z-5`}
            >
                <motion.div variants={slideLeft(desktopDelay)} className="text-white text-sm">
                    <p>14日間、共に。</p>
                    <p>カード登録不要。</p>
                </motion.div>

                <motion.div
                    variants={line(desktopDelay)}
                    style={{ transformOrigin: "top" }}
                    className="h-[50px] w-[1px] bg-[#9FC183]"
                />

                <motion.div variants={slideRight(desktopDelay)}>
                    <Button 
                        className="bg-[#27485C] text-[#F2EACE] font-light tracking-wider" 
                        // onPress={() => navigate("/signup")}
                    >
                        aomeを体験
                    </Button>
                </motion.div>
            </motion.div>
        </>
    );
};
const IntroSection = ({ introRef }) => {
    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };
    return (
        <div className="h-max mt-[20px] md:mt-[150px] scroll-mt-[40px] md:scroll-mt-[90px]" ref={introRef} id="about-us">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                className="flex flex-col font-medium text-white text-[24px] w-full items-start pl-7 md:hidden"
            >
                <p>言葉ってなんですか。</p>
                <p>学ぶってなんですか。</p>
            </motion.div>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                className="hidden md:flex flex-col font-medium text-white text-[26px] w-full items-center"
            >
                <p>言葉ってなんですか。学ぶってなんですか。</p>
            </motion.div>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.15 }}
                className="tracking-widest flex flex-col mt-1 italic font-light text-base-text text-[13px] w-full items-start pl-7 md:hidden"
            >
                <p>What even is language? </p>
                <p>What does it mean to learn one?</p>
            </motion.div>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.15 }}
                className="tracking-widest hidden md:flex mt-1 flex-col italic font-light text-base-text text-[14px] w-full items-center"
            >
                <p>What even is language? What does it mean to learn one?</p>
            </motion.div>



            <div className="mt-10 flex flex-col gap-10 items-center px-7 text-left text-[16px] tracking-wider leading-[1.6] font-normal text-white">
                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="md:max-w-[715px]"
                >
                    言葉は、誰かが設計したものではありません。何千年もの間、ただ人々が、伝えたいことを伝えようとしてきた。その積み重ねが、言葉になりました。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="md:max-w-[715px]"
                >
                    学ぶということも、同じです。知らない言葉に出会い、その意味を組み立てようとすること。言いたいことがあり、それをまだ言えないと気づき、それでも言葉を探し続けること。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="md:max-w-[715px]"
                >
                    積み重ねでできたものは、積み重ねでしか、自分のものになりません。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="md:max-w-[715px]"
                >
                    そのどちらの瞬間にも、深く観察すること。それが、言葉が自分のものになる、ただ一つの道です。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="md:max-w-[715px]"
                >
                    これは、私たちがaomeを作りながら、実際にたどり着いた理解です。誰かに教えられたのではなく、問い続けることで見えてきたもの。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                >
                    アメリカとウクライナから来た23歳と20歳の夫婦が、日本でaomeを創りました。
                </motion.p>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="w-full md:w-max"
                >
                    aomeは、その全てを一つにした場所です。
                </motion.p>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="w-full"
                >
                    <Link
                        className="text-right text-[#A2E9F7] cursor-pointer flex justify-center items-center gap-1"
                        to={"/about-us"}
                    >
                        ふたりのことを、もう少し
                        <ChevronRight className="text-[#A2E9F7] size-4" strokeWidth={1.8} />
                    </Link>
                </motion.div>
            </div>
        </div>
    )
}
const InfoSection = () => {
    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    const dotPop = {
        hidden: { opacity: 0, scale: 0.4 },
        show: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    const lineRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: lineRef,
        offset: ["start 0.8", "end 0.5"], // tweak these — see explanation below
    });

    const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <div className="h-max mt-24 md:mt-[150px] md:pl-[10vw] lg:pl-[15vw] flex flex-row items-start">
            <div className="relative flex flex-col items-center ml-4 md:ml-5 shrink-0 self-stretch">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.6 }}
                    variants={dotPop}
                    className="mt-1.5 rounded-full w-5 h-5 md:w-6 md:h-6 bg-[#DCCF88] shrink-0"
                />

                <motion.div
                    ref={lineRef}
                    style={{ scaleY, transformOrigin: "top" }}
                    className="w-[2px] flex-1 mt-2 mb-1 bg-[#DCCF88] rounded-full"
                />
            </div>

            <div className="mt-5">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={fadeUp}
                    transition={{ delay: 0.1 }}
                    className="mt-2 flex flex-col font-medium text-white tracking-wider text-[17px] w-full items-start pl-2 md:hidden"
                >
                    <p>英語を知っている。でも、話せない。</p>
                    <p>文法も、単語も、頭には入っている。</p>
                    <p>それでも、いざとなると言葉が出てこない。</p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={fadeUp}
                    transition={{ delay: 0.1 }}
                    className="ml-2 hidden md:flex flex-col font-medium text-white tracking-wider text-[17px] w-max items-start"
                >
                    <p>英語を知っている。でも、話せない。</p>
                    <p>文法も、単語も、頭には入っている。</p>
                    <p>それでも、いざとなると言葉が出てこない。</p>
                </motion.div>

                <div className="mt-10 flex flex-col gap-6 items-center md:items-start pl-2 pr-4 text-left tracking-wider text-[17px] font-normal text-white">
                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeUp}
                        className="w-full md:max-w-[715px]"
                    >
                        それは、性格でも、才能でもありません。
                    </motion.p>

                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeUp}
                        className="w-full md:max-w-[715px]"
                    >
                        話すこと自体は、ただの運動技能です。息継ぎのタイミング、口の動かし方——数週間もあれば身につきます。
                    </motion.p>

                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeUp}
                        className="w-full md:max-w-[715px]"
                    >
                        本当に難しいのは、その瞬間に何を言うべきかを、感覚として知っていること。
                    </motion.p>

                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeUp}
                        className="w-full md:max-w-[715px]"
                    >
                        その感覚は、ルールを覚えることでは、育ちません。本物の言葉に何度も出会い、深く観察すること。そして、言いたいことを、自分の力で言葉にしようと、繰り返し試みること。その両方でしか、身についていかない感覚です。
                    </motion.p>

                    <motion.p
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeUp}
                        className="w-full md:max-w-[715px]"
                    >
                        まだ、その感覚に、十分出会っていないだけです。
                    </motion.p>
                </div>
            </div>
        </div>
    )
}
const PricingBoxes = () => {
    const pricingContainer = {
        hidden: {},
        show: {
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.1,
            },
        },
    };

    const pricingCard = {
        hidden: { opacity: 0, x: -8 },
        show: (delay = 0) => ({
            opacity: 1,
            x: 0,
            transition: { duration: 0.3, ease: "easeOut" },
        }),
    };

    return (
        <motion.div
            variants={pricingContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="flex flex-col md:flex-row md:justify-center md:pl-[100px] items-center gap-5"
        >
            {/* monthly */}
            <motion.div
                variants={pricingCard}
                className="bg-white/40 backdrop-blur rounded-3xl shadow-sm p-5 w-full md:w-[350px]"
            >
                <div className="flex items-center gap-2">
                    <div className="rounded-full w-5 h-5 shrink-0 bg-[#78B0D0]" />
                    <div className="rounded-full w-[30px] h-[1px] shrink-0 bg-[#78B0D0]" />
                    <span className="text-[22px] font-light text-base-text">月額 </span>
                </div>

                <div className="mt-8 flex flex-col">
                    <span className="pl-1 text-[20px] text-base-text line-through decoration-white decoration-1">3,900円</span>
                    <span className="text-[40px] tracking-wide text-base-text flex gap-1 items-end">2,900円
                        <span className="text-[12px] pb-2.5 tracking-normal">税込</span>
                    </span>
                </div>

                <div className="mt-6 flex flex-col">
                    <span className="pl-1 text-[12px] text-base-text font-light">自動更新、</span>
                    <span className="pl-1 text-[12px] text-base-text font-light">いつでも簡単解約。</span>
                </div>
            </motion.div>

            {/* yearly */}
            <motion.div
                variants={pricingCard}
                custom={0.3}
                className="bg-white/40 backdrop-blur rounded-3xl shadow-sm p-5 w-full md:w-[350px]"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="rounded-full w-5 h-5 shrink-0 bg-[#AAA2CD]" />
                        <div className="rounded-full w-[30px] h-[1px] shrink-0 bg-[#AAA2CD]" />
                        <span className="text-[22px] font-light text-base-text">年額</span>
                    </div>

                    <div className="flex items-center gap-1">
                        <div className="flex flex-col items-end text-base-text font-normal text-[13px]">
                            <span>月額換算</span>
                            <span>2,083円</span>
                        </div>
                        <div className="w-[2px] h-[35px] bg-[#AAA2CD]" />
                    </div>
                </div>

                <div className="mt-8 flex flex-col">
                    <span className="pl-1 text-[20px] text-base-text line-through decoration-white decoration-1">33,000円</span>
                    <span className="text-[40px] tracking-wide text-base-text flex gap-1 items-end">25,000円
                        <span className="text-[12px] pb-2.5 tracking-normal">税込</span>
                    </span>
                </div>

                <div className="mt-6 flex flex-col">
                    <span className="pl-1 text-[12px] text-base-text font-light">自動更新、</span>
                    <span className="pl-1 text-[12px] text-base-text font-light">いつでも簡単解約。</span>
                </div>
            </motion.div>
        </motion.div>
    )
}


const LandingPageContent = () => {
    const vh = useStableViewportHeight();
    const navigate = useNavigate()
    const location = useLocation();

    const [isLandingPageModalOpen, setIsLandingPageModalOpen] = useState(false);
    useEffect(() => {
        if (location.pathname === '/faq' ||
            location.pathname === '/commerce-disclosure' ||
            location.pathname === '/privacy-policy' ||
            location.pathname === '/terms-of-service' ||
            location.pathname === '/about-us'
        ) {
            setIsLandingPageModalOpen(true);
        }
    }, [location.pathname]);
    const handleLandingPageModalClose = () => {
        navigate(`/`);
        setIsLandingPageModalOpen(false);
    }

    const introRef = useRef(null);
    const scrollToIntro = () => {
        introRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start", // Aligns the top of the section with the top of the viewport
        });
    };

    const closingContainer = {
        hidden: {},
        show: {
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1,
            },
        },
    };

    const closingLine = {
        hidden: { opacity: 0, y: 12 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    };
    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    return (
        <>
            {/* navbar */}
            <div className="w-full p-[0.4rem] md:p-[1rem] top-0 sticky z-50">
                <WelcomeNavbar />
            </div>

            {/* hero section */}
            <div
                className={`h-[740px] md:h-[calc(100svh_-_92.8px)] lg:h-[calc(100svh_-_105.6px)] relative flex flex-col justify-center`}
            >

                {/* mobile */}
                {/* <div className={`absolute 
                    ${getClass(vh, "top-[190px]", "top-[167px]")} 
                    [@media(min-width:541px)_and_(max-width:765px)]:top-[50px] 
                    [@media(min-width:450px)_and_(max-width:540px)]:top-[140px] 
                    [@media(min-width:450px)_and_(max-width:765px)]:h-auto
                    h-[550px] w-full 
                    md:hidden 
                    overflow-hidden 
                    [@media(min-height:700px)]:max-h-[550px]`}>
                    <img
                        src="/landing-page/clouds-mobile.png"
                        alt="Clouds Illustration"
                        className="w-full h-auto object-cover object-top"
                    />
                </div> */}
                <div className={`absolute 
                ${getClass(vh, "top-[190px]", "top-[167px]")} 
                [@media(min-width:541px)_and_(max-width:765px)]:top-[50px] 
                [@media(min-width:450px)_and_(max-width:540px)]:top-[140px] 
                [@media(min-width:450px)_and_(max-width:765px)]:h-auto
                h-[550px] w-full 
                md:hidden 
                overflow-hidden 
                [@media(min-height:700px)]:max-h-[550px]
                [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]
                [-webkit-mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]`}>
                    <img
                        src="/landing-page/clouds-mobile.webp"
                        alt="Clouds Illustration"
                        className="w-full h-auto object-cover object-top"
                    />
                </div>

                <img
                    className={`absolute top-0 right-0 ${getClass(vh, "h-[570px]", "h-[500px]")} md:hidden`}
                    src="landing-page/balloon-mobile.webp"
                    alt="Balloon Illustration"
                />

                {/* desktop */}
                <img
                    className={`absolute bottom-0 w-full h-auto hidden md:block`}
                    src="landing-page/clouds-desktop.webp"
                    alt="Clouds Illustration"
                />
                {/* <img
    className={`absolute bottom-0 w-full h-auto hidden md:block
                [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]
                [-webkit-mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]`}
    src="landing-page/clouds-desktop.png"
    alt="Clouds Illustration"
/> */}
                <img
                    className={`absolute md:-top-[92.8px] lg:-top-[105.6px] h-[740px] [@media(max-height:600px)]:h-[90svh] right-0 hidden md:block`}
                    src="landing-page/balloon-desktop.webp"
                    alt="Balloon Illustration"
                />

                {/* cta */}
                <Cta vh={vh} navigate={navigate} />


                {/* hero text */}
                <HeroText vh={vh} />


                {/* chevron down */}
                <div className={`md:hidden w-full flex justify-center absolute ${getClass(vh, "top-[calc(553px_+_130px)]", "top-[calc(553px_+_107px)]")}`}>
                    <Button variant="light" size="sm" isIconOnly onPress={scrollToIntro}>
                        <ChevronDown className="text-base-text size-6 animate-bounce" strokeWidth={1.5} />
                    </Button>
                </div>

                <div className={`hidden w-full md:flex justify-center absolute top-[80svh]`}>
                    <Button variant="light" size="sm" isIconOnly onPress={scrollToIntro}>
                        <ChevronDown className="text-base-text size-6 animate-bounce" strokeWidth={1.5} />
                    </Button>
                </div>

            </div>


            {/* intro section */}
            <IntroSection introRef={introRef} />


            {/* section with the ball (quote) */}
            <InfoSection />


            {/* feature section */}
            <div className="mt-24 md:mt-[150px] h-max scroll-mt-[60px]" id="features">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    className="flex flex-col font-medium text-white text-[30px] w-full items-start pl-7 md:hidden">
                    <p className="w-full">世界は、</p>
                    <p lassName="w-full">こうして広がっていく。</p>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    className="hidden md:flex flex-col font-medium text-white text-[26px] w-full items-center">
                    <p>世界は、こうして広がっていく。</p>
                </motion.div>

                <div className="mt-16 md:mt-12 md:flex flex-col w-full items-center">
                    <Feature1 className="md:max-w-[800px] lg:max-w-[1000px]" />
                    <Feature2 className="md:max-w-[800px] lg:max-w-[1000px]" />
                    <Feature3 className="md:max-w-[800px] lg:max-w-[1000px]" />
                    <Feature4 className="md:max-w-[800px] lg:max-w-[1000px]" />
                    <Feature5 className="md:max-w-[800px] lg:max-w-[1000px]" />
                </div>


                <motion.div
                    variants={closingContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.5 }}
                    className="mt-10 flex flex-col w-full items-center text-white text-[15px]"
                >
                    <motion.p variants={closingLine}>はじめの一語から、ずっと。</motion.p>
                    <motion.p variants={closingLine}>aomeは、ここにいます。</motion.p>
                </motion.div>
            </div>


            {/* pricing */}
            <div className="mt-20 md:mt-[150px] h-max flex flex-col gap-8 px-7" id="pricing">
                <div className="md:flex flex-col items-center">
                    <div>
                        <p className="text-white text-[30px] w-full text-center">創設メンバー限定価格</p>
                        <p className="pl-3 md:pl-0 mt-3 text-base-text font-light text-sm text-center">aomeの始まりを支えてくださる最初の100名様へ、</p>
                        <p className="pl-3 md:pl-0 mt-1 text-base-text font-light text-sm text-center">感謝を込めて。ずっとこの価格で。</p>
                    </div>

                </div>

                {/* pricing boxes */}
                <PricingBoxes />

                {/* button */}
                <div className="flex flex-col md:flex-row w-full items-center md:items-start md:justify-center mt-3 gap-2">
                    <p className="tracking-wider bg-white/40 backdrop-blur rounded-2xl p-2 text-base-text text-[14px] w-max md:w-max text-center md:hidden">14日間、無料。カード登録不要。</p>

                    <div className="hidden md:flex gap-1 mt-1">
                        <div className="flex flex-col items-end text-white lg:text-base-text text-[12px]">
                            <p>14日間、無料。</p>
                            <p>カード登録不要。</p>
                        </div>

                        <div className="w-[1px] h-[40px] bg-[#5995B8]" />
                    </div>

                    <div className="flex flex-col w-full md:w-max items-center gap-2">
                        <Button 
                            className="bg-[#27485C] text-[16px] w-full md:w-[350px] text-white font-extralight rounded-2xl h-12" 
                            // onPress={() => navigate("/signup")}
                        >
                            すぐ始める
                        </Button>

                        <span className="text-[12px] text-base-text flex gap-2 items-center justify-center w-full">
                            <Lock strokeWidth={1.5} className="size-4 text-base-text" />
                            安全なお支払い
                            <span>|</span>
                            <img src="stripe-blurple-sm.png" alt="Stripe Logo" className="h-4" />
                        </span>
                    </div>

                </div>
            </div>


            {/* faq */}
            <div className="tracking-wider mt-[200px] md:hidden md:mt-20 flex flex-col text-sm text-white px-5 gap-2">
                <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=1">英語初心者でも使えますか <ChevronRight className="text-white size-4" strokeWidth={1} /></Link>
                <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=2">どのくらいで話せるようになりますか <ChevronRight className="text-white size-4" strokeWidth={1} /></Link>
                <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=3">1日どのくらい必要ですか <ChevronRight className="text-white size-4" strokeWidth={1} /></Link>
                <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=4">TOEICなどの試験対策になりますか <ChevronRight className="text-white size-4" strokeWidth={1} /></Link>
                <Link className="cursor-pointer flex gap-1 items-center" to="/faq">その他のご質問 <ChevronRight className="text-white size-4" strokeWidth={1} /></Link>
            </div>

            {/* footer */}
            <footer
                id="footer"
                className="md:flex justify-between items-end w-full p-5 mt-[20px]">

                <div className="flex flex-col gap-3">
                    <div className="flex flex-col w-full gap-2 tracking-wider">
                        <FooterLogo />
                        <div className="text-sm text-white ">hello@aome.co.jp</div>
                        <div className="flex gap-1 items-center -mt-1.5">
                            <span className="text-white text-sm">aome合同会社</span>
                            <span className="text-white text-sm">|</span>
                            <span className="text-white text-sm">東京都豊島区池袋二丁目11番9号</span>
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row items-start gap-2 lg:gap-8 tracking-wider">
                        <Link className="text-white text-sm" to="/commerce-disclosure">特定商取引法に基づく表記</Link>
                        <div className="flex gap-8 lg:gap-8">
                            <Link className="text-white text-sm" to="/privacy-policy">プライバシーポリシー</Link>
                            <Link className="text-white text-sm" to="/terms-of-service">利用規約</Link>
                        </div>


                    </div>
                </div>

                <div className="tracking-wider mt-[300px] hidden md:mt-20 md:flex flex-col items-end text-sm text-base-text font-light pl-5 gap-2">
                    <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=1">英語初心者でも使えますか <ChevronRight className="text-base-text size-4" strokeWidth={1} /></Link>
                    <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=2">どのくらいで話せるようになりますか <ChevronRight className="text-base-text size-4" strokeWidth={1} /></Link>
                    <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=3">1日どのくらい必要ですか <ChevronRight className="text-base-text size-4" strokeWidth={1} /></Link>
                    <Link className="cursor-pointer flex gap-1 items-center" to="/faq?q=4">TOEICなどの試験対策になりますか <ChevronRight className="text-base-text size-4" strokeWidth={1} /></Link>
                    <Link className="cursor-pointer flex gap-1 items-center" to="/faq">その他のご質問 <ChevronRight className="text-base-text size-4" strokeWidth={1} /></Link>
                </div>

                <LandingPageModal
                    isOpen={isLandingPageModalOpen}
                    onClose={handleLandingPageModalClose}
                />
            </footer>
        </>
    )
}

const WelcomePage = () => {


    useEffect(() => {
        const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setVH();

        window.addEventListener('orientationchange', setVH);

        return () => {
            window.removeEventListener('orientationchange', setVH);
        };
    }, []);

    const desktopBgUrl = `${process.env.PUBLIC_URL}/landing-page/aome-on-mountain-desktop.webp`;
    const mobileBgUrl = `${process.env.PUBLIC_URL}/landing-page/aome-on-mountain-mobile-1.webp`;

    return (
        <div
            className="h-max w-full relative bg-no-repeat welcome-bg"
            style={{
                '--desktop-bg': `url(${desktopBgUrl})`,
                '--mobile-bg': `url(${mobileBgUrl})`,
                backgroundImage: `var(--mobile-bg), linear-gradient(167deg, #5995B8 0%, #5995B8 80%, #BCDDDE 100%)`,
                backgroundPosition: "bottom, center",
                backgroundSize: "contain, cover",
                backgroundRepeat: "no-repeat, no-repeat",
            }}
        >
            {/* Rendered ONLY ONCE - solves modal duplication */}
            <LandingPageContent />
        </div>
    );

    // return (
    //     <>
    //         {/* mobile */}
    //         <div
    //             className="h-max w-full relative bg-no-repeat block md:hidden"
    //             style={{
    //                 backgroundImage: `url(${process.env.PUBLIC_URL}/landing-page/aome-on-mountain-mobile.png), linear-gradient(167deg, #5995B8 0%, #5995B8 50%, #BCDDDE 100%)`,
    //                 backgroundPosition: "bottom, center",
    //                 backgroundSize: "contain, cover",
    //                 backgroundRepeat: "no-repeat, no-repeat",
    //             }}
    //         >
    //             <LandingPageContent />
    //         </div>

    //         {/* tablet + desktop */}
    //         <div
    //             className="h-max w-full relative bg-no-repeat hidden md:block lg:hidden"
    //             style={{
    //                 backgroundImage: `url(${process.env.PUBLIC_URL}/landing-page/aome-on-mountain-desktop.png), linear-gradient(167deg, #5995B8 0%, #5995B8 50%, #BCDDDE 100%)`,
    //                 backgroundPosition: "bottom left, center",
    //                 backgroundSize: "320px auto, cover",
    //                 backgroundRepeat: "no-repeat, no-repeat",
    //             }}
    //         >
    //             <LandingPageContent />
    //         </div>

    //         <div
    //             className="h-max w-full relative bg-no-repeat hidden lg:block"
    //             style={{
    //                 backgroundImage: `url(${process.env.PUBLIC_URL}/landing-page/aome-on-mountain-desktop.png), linear-gradient(167deg, #5995B8 0%, #5995B8 50%, #BCDDDE 100%)`,
    //                 backgroundPosition: "bottom left, center",
    //                 backgroundSize: "500px auto, cover",
    //                 backgroundRepeat: "no-repeat, no-repeat",
    //             }}
    //         >
    //             <LandingPageContent />
    //         </div>
    //     </>


    // )
}
export default WelcomePage;